export function reportNumber(value) {
  if (value == null || typeof value === "boolean" || String(value).trim() === "") return null
  const number = Number(String(value).replace(/,/g, ""))
  return Number.isFinite(number) ? number : null
}

export function reportKpi(table, data) {
  if (!table) return null
  if (data?.aggregation === "count") return table.rows.length
  const raw = table.rows.map((row) => reportNumber(row[data?.metric]))
  if (data?.aggregation === "first") return raw[0] ?? null
  if (data?.aggregation === "last") return raw.at(-1) ?? null
  const values = raw.filter((value) => value !== null)
  if (!values.length) return null
  if (data?.aggregation === "min") return Math.min(...values)
  if (data?.aggregation === "max") return Math.max(...values)
  const total = values.reduce((sum, value) => sum + value, 0)
  return data?.aggregation === "avg" ? total / values.length : total
}

// Each continuation contains a complete table row and repeats the original header.
export function paginateReportTab(tab, tables, canvas) {
  const first = { ...tab, tables: { ...tables } }
  const pages = [first]
  for (const widget of tab.widgets || []) {
    const table = tables[widget.id]
    if (!["table", "freeTable"].includes(widget.type) || !table) continue
    const capacity = Math.max(1, Math.floor((widget.layout.h - 80) / 36))
    first.tables[widget.id] = { ...table, rows: table.rows.slice(0, capacity) }
    const height = (canvas?.height || 794) - 170
    const continuationCapacity = Math.max(1, Math.floor((height - 80) / 36))
    for (let offset = capacity; offset < table.rows.length; offset += continuationCapacity) {
      pages.push({ id: `${tab.id}-${widget.id}-${offset}`, name: tab.name,
        widgets: [...tab.widgets.filter((item) => item.type === "header"), { ...widget, layout: { x: 32, y: 120, w: (canvas?.width || 1123) - 64, h: height } }],
        tables: { [widget.id]: { ...table, rows: table.rows.slice(offset, offset + continuationCapacity) } } })
    }
  }
  return pages
}

export async function renderReportPdf(root, config) {
  await document.fonts.ready
  await Promise.all([...root.querySelectorAll("img")].map((image) => image.decode()))
  await new Promise((resolve, reject) => {
    const deadline = performance.now() + 15000
    const ready = () => {
      const charts = [...root.querySelectorAll("[data-report-chart]")]
      const kpis = [...root.querySelectorAll("[data-report-kpi]")]
      if (kpis.some((kpi) => kpi.dataset.reportKpi === "error")) { reject(new Error("KPI calculation failed")); return }
      if (charts.every((chart) => chart.dataset.reportChart === "ready") && kpis.every((kpi) => kpi.dataset.reportKpi === "ready")) {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
        return
      }
      if (performance.now() > deadline) { reject(new Error("Chart rendering timed out")); return }
      requestAnimationFrame(ready)
    }
    ready()
  })
  const { default: html2canvas } = await import("html2canvas")
  const { jsPDF } = await import("jspdf")
  const portrait = config.canvas?.orientation === "portrait"
  const pdf = new jsPDF({ orientation: portrait ? "portrait" : "landscape", unit: "mm", format: "a4", compress: true })
  const width = pdf.internal.pageSize.getWidth()
  const height = pdf.internal.pageSize.getHeight()
  const pages = [...root.querySelectorAll(".datox-react-dashboard__export-page")]
  if (!pages.length) throw new Error("No export pages")
  for (const [index, page] of pages.entries()) {
    const canvas = await html2canvas(page, { backgroundColor: "#ffffff", scale: 2, useCORS: true })
    if (index) pdf.addPage()
    const scale = Math.min(width / canvas.width, height / canvas.height)
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width * scale, canvas.height * scale, undefined, "FAST")
  }
  return pdf.output("blob")
}
